// src/components/Keyboard.js
import React from 'react';
import { BsBackspace, BsArrowCounterclockwise } from 'react-icons/bs';

const Keyboard = ({ onKeyPress, onUndo, letterStates, currentTier = 'default' }) => {
  const keys = [
    "QWERTYUIOP",
    "ASDFGHJKL",
    "ZXCVBNM"
  ];

  const baseKeyClass = "h-14 rounded-md flex items-center justify-center font-bold uppercase text-xs sm:text-base relative transition-colors";

  // Determine dynamic color based on tier
  const getDynamicColorClass = () => {
      switch (currentTier) {
          case 'bronze': return 'bg-bronze dark:bg-bronze text-white';
          case 'silver': return 'bg-silver dark:bg-silver text-gray-800';
          case 'gold': return 'bg-gold dark:bg-gold text-gray-900';
          case 'platinum': return 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white';
          default: return 'bg-correct dark:bg-correct text-white';
      }
  };

  const dynamicColorClass = getDynamicColorClass();

  const getKeyClass = (key) => {
    const stateInfo = letterStates[key] || { state: 'unused', count: 0 };
    const state = stateInfo.state;
    
    switch (state) {
        case 'starter':
        case 'used':
        case 'typing':
            return `${baseKeyClass} ${dynamicColorClass}`;
        default: // unused
            return `${baseKeyClass} bg-key-bg dark:bg-dark-key-bg hover:bg-gray-400 dark:hover:bg-gray-500`;
    }
  };

  const specialKeyClass = `${baseKeyClass} bg-key-bg dark:bg-dark-key-bg hover:bg-gray-400 dark:hover:bg-gray-500`;

  return (
    <div className="flex flex-col items-center mt-4 w-full max-w-2xl mx-auto px-1 space-y-1">
      {/* Top Row */}
      <div className="flex justify-center w-full space-x-1">
        {keys[0].split('').map((key) => {
          const stateInfo = letterStates[key] || { state: 'unused', count: 0 };
          return (
            <button key={key} onClick={(e) => { onKeyPress(key); e.currentTarget.blur(); }} className={`flex-1 ${getKeyClass(key)}`}>
              {key}
              {stateInfo.count > 1 && (
                <span className="absolute -top-1 -right-1 text-xs text-background dark:text-dark-background bg-accent dark:bg-dark-accent rounded-full h-5 w-5 flex items-center justify-center">
                  {stateInfo.count}
                </span>
              )}
            </button>
          );
        })}
        <button onClick={(e) => { onUndo(); e.currentTarget.blur(); }} className={`${specialKeyClass} w-12`}>
          Undo
        </button>
      </div>
      
      {/* Middle Row */}
      <div className="flex justify-center w-full space-x-1">
        <div className="w-6" /> {/* Spacer */}
        {keys[1].split('').map((key) => {
          const stateInfo = letterStates[key] || { state: 'unused', count: 0 };
          return (
            <button key={key} onClick={(e) => { onKeyPress(key); e.currentTarget.blur(); }} className={`flex-1 ${getKeyClass(key)}`}>
              {key}
              {stateInfo.count > 1 && (
                <span className="absolute -top-1 -right-1 text-xs text-background dark:text-dark-background bg-accent dark:bg-dark-accent rounded-full h-5 w-5 flex items-center justify-center">
                  {stateInfo.count}
                </span>
              )}
            </button>
          );
        })}
        <div className="w-6" /> {/* Spacer */}
      </div>

      {/* Bottom Row */}
      <div className="flex justify-center w-full space-x-1">
        <button onClick={(e) => { onKeyPress('ENTER'); e.currentTarget.blur(); }} className={`${specialKeyClass} w-16`}>
            Enter
        </button>
        {keys[2].split('').map((key) => {
          const stateInfo = letterStates[key] || { state: 'unused', count: 0 };
          return (
            <button key={key} onClick={(e) => { onKeyPress(key); e.currentTarget.blur(); }} className={`flex-1 ${getKeyClass(key)}`}>
              {key}
              {stateInfo.count > 1 && (
                <span className="absolute -top-1 -right-1 text-xs text-background dark:text-dark-background bg-accent dark:bg-dark-accent rounded-full h-5 w-5 flex items-center justify-center">
                  {stateInfo.count}
                </span>
              )}
            </button>
          );
        })}
        <button onClick={(e) => { onKeyPress('DELETE'); e.currentTarget.blur(); }} className={`${specialKeyClass} w-16`}>
            <BsBackspace size="1.5em" />
        </button>
      </div>
    </div>
  );
};

export default Keyboard;
